/**
 * Crew scheduled briefing (F6) — prefs jsonb helpers
 *
 * Stored under pulse_preferences.card_type_preferences.meta.briefing_schedule
 * so we avoid a migration for the spike. Pure validate / normalize / due
 * evaluation — no queue vendor, no duplicate PulseAgentService.
 *
 * Existing cron that already calls PulseAgentService.generatePulseCards:
 *   app/api/cron/pulse-generation/route.ts
 * Midday interest pass calls `runScheduledBriefingInterestRefresh`
 * (feed-refresh.ts F6 PREP facade: worker-shape due+env → gate →
 * `refreshInterestLaneCards`). Persist stays env-gated via
 * `CREW_MIDDAY_INTEREST_REFRESH=1`.
 *
 * Full standalone RRULE job runner is NOT SHIPPED — pure due helpers +
 * `resolveScheduledBriefingWorkerShape` / midday PREP facade only. Do not
 * invent a sibling cron process here.
 *
 * Soft lifecycle (architecture §1d): `on_change` empty skips increment
 * `emptySkipCount`; after `CREW_BRIEFING_EMPTY_SKIP_AUTO_PAUSE_AFTER` quiet
 * checks the midday path pauses the schedule (`enabled=false`, `autoPaused`)
 * via `applyCrewBriefingOnChangeEmptySkipLifecycle` — still the same cron.
 *
 * Vercel-registered sibling (batch / EnhancedPulseAgentService, every 15m):
 *   app/api/cron/pulse-batch-generation/route.ts
 * Notification digests (task reminders — not Pulse cards):
 *   app/api/cron/notifications/route.ts
 *
 * Dependencies: none (pure)
 */

/**
 * Repo-relative path of the cron route that should honor Crew briefing
 * schedules by calling PulseAgentService.generatePulseCards (already does).
 */
export const CREW_BRIEFING_CRON_ENTRYPOINT =
  'app/api/cron/pulse-generation/route.ts' as const

/** Soft cap on “monitor web for X” query text. */
export const CREW_BRIEFING_MONITOR_QUERY_MAX = 500

/** Soft cap on schedule expression length. */
export const CREW_BRIEFING_SCHEDULE_MAX = 160

/**
 * Monitor fire policy (F6 / ChatGPT Scheduled “notify on change” gap).
 * `cadence` = always regenerate on due tick (default — prod cron unchanged).
 * `on_change` = skip interest-lane insert when research finds no delta.
 */
export const CREW_BRIEFING_MONITOR_MODE_CADENCE = 'cadence' as const
export const CREW_BRIEFING_MONITOR_MODE_ON_CHANGE = 'on_change' as const
export const CREW_BRIEFING_MONITOR_MODES = [
  CREW_BRIEFING_MONITOR_MODE_CADENCE,
  CREW_BRIEFING_MONITOR_MODE_ON_CHANGE
] as const
export type CrewBriefingMonitorMode =
  (typeof CREW_BRIEFING_MONITOR_MODES)[number]
/** Safe default — existing schedules stay cadence until prefs opt in. */
export const CREW_BRIEFING_MONITOR_MODE_DEFAULT =
  CREW_BRIEFING_MONITOR_MODE_CADENCE

/**
 * Normalize unknown jsonb / form input into a closed monitor mode.
 * Unknown / missing → cadence (never invent on_change).
 */
export function normalizeCrewBriefingMonitorMode(
  input: unknown
): CrewBriefingMonitorMode {
  if (typeof input !== 'string') return CREW_BRIEFING_MONITOR_MODE_DEFAULT
  const key = input.trim().toLowerCase().replace(/-/g, '_')
  if (key === CREW_BRIEFING_MONITOR_MODE_ON_CHANGE) {
    return CREW_BRIEFING_MONITOR_MODE_ON_CHANGE
  }
  if (key === CREW_BRIEFING_MONITOR_MODE_CADENCE) {
    return CREW_BRIEFING_MONITOR_MODE_CADENCE
  }
  return CREW_BRIEFING_MONITOR_MODE_DEFAULT
}

/** True only for normalized `on_change` (strict — soft aliases go through normalize). */
export function isOnChangeCrewBriefingMonitorMode(input: unknown): boolean {
  return (
    normalizeCrewBriefingMonitorMode(input) ===
    CREW_BRIEFING_MONITOR_MODE_ON_CHANGE
  )
}

/** Schedule section heading — web + mobile CrewInterestBrief (lockstep). */
export const CREW_BRIEFING_SECTION_LABEL = 'Schedule'

/** Collapse control when the schedule block is open (mobile; web uses ±). */
export const CREW_BRIEFING_SECTION_HIDE_LABEL = 'Hide'

/** Enable toggle / switch label — web + mobile CrewInterestBrief (lockstep). */
export const CREW_BRIEFING_ENABLE_LABEL =
  'Enable'

/** Schedule expression field label. */
export const CREW_BRIEFING_SCHEDULE_FIELD_LABEL =
  'Schedule'

/** Monitor query field label. */
export const CREW_BRIEFING_MONITOR_FIELD_LABEL = 'Topic'

/** Primary save CTA for the schedule block. */
export const CREW_BRIEFING_SAVE_LABEL = 'Save'

/** Accessibility label for the schedule save control. */
export const CREW_BRIEFING_SAVE_A11Y_LABEL = 'Save'

/**
 * Default cron expression for DEFAULT_SCHEDULE.schedule (enabled-ready).
 * Keep separate from PLACEHOLDER — placeholder ends with … per a11y copy rules.
 */
export const CREW_BRIEFING_SCHEDULE_DEFAULT = '0 7 * * *' as const

/**
 * Schedule field placeholder — example pattern ending with … (Web Interface
 * Guidelines). Do not use as the persisted default expression.
 */
export const CREW_BRIEFING_SCHEDULE_PLACEHOLDER = '0 7 * * *…' as const

/** Example monitor query placeholder — silenced (anti-slop). */
export const CREW_BRIEFING_MONITOR_PLACEHOLDER = '' as const

/**
 * Monitor mode group label — cadence vs on_change (human copy, not jargon).
 * CrewInterestBrief radiogroup (web + mobile).
 */
export const CREW_BRIEFING_MONITOR_MODE_FIELD_LABEL = 'When'

/** Cadence option — always regenerate on due tick. */
export const CREW_BRIEFING_MONITOR_MODE_CADENCE_LABEL = 'Due'

/**
 * On-change option — skip quiet / empty / no-delta interest-lane inserts.
 * Not change-detection product framing ("Only when something changes").
 */
export const CREW_BRIEFING_MONITOR_MODE_ON_CHANGE_LABEL =
  'Change'

/** Hint under the mode toggle — explains both options in plain language. */
export const CREW_BRIEFING_MONITOR_MODE_HINT = '' as const

/** Monitor mode radiogroup `name` (web form hygiene). */
export const CREW_BRIEFING_MONITOR_MODE_FIELD_NAME =
  'crew_briefing_monitor_mode' as const

/** Monitor mode group label / radiogroup labelling id. */
export const CREW_BRIEFING_MONITOR_MODE_GROUP_ID =
  'crew-briefing-monitor-mode' as const

/** Monitor mode hint id — web `aria-describedby`. */
export const CREW_BRIEFING_MONITOR_MODE_HINT_ID =
  'crew-briefing-monitor-mode-hint' as const

/** Schedule expression input `name` (web autofill hygiene). */
export const CREW_BRIEFING_SCHEDULE_FIELD_NAME =
  'crew_briefing_schedule' as const

/** Monitor query input `name` (web autofill hygiene). */
export const CREW_BRIEFING_MONITOR_FIELD_NAME = 'crew_briefing_monitor' as const

/**
 * Expandable schedule panel DOM id — web `aria-controls` + panel `id`
 * (CrewInterestBrief). Metro cannot import web/ — mobile mirrors the constant.
 */
export const CREW_BRIEFING_SCHEDULE_PANEL_ID =
  'crew-briefing-schedule-panel' as const

/** Status line id — next run / paused (web CrewInterestBrief; mobile mirror). */
export const CREW_BRIEFING_STATUS_ID = 'crew-briefing-schedule-status' as const

/** Prefix before formatted next-run timestamp. */
export const CREW_BRIEFING_NEXT_RUN_PREFIX = 'Next' as const

/** Status when schedule is saved but disabled (pause). */
export const CREW_BRIEFING_STATUS_PAUSED =
  'Paused' as const

/**
 * Status when soft lifecycle auto-paused after N quiet on_change checks.
 * Surfaced in InterestBrief + Your-crew Scheduled list — factual Paused.
 */
export const CREW_BRIEFING_STATUS_AUTO_PAUSED = 'Paused' as const

/** Prefix before quiet-check streak (`Quiet checks · 2 of 3`). */
export const CREW_BRIEFING_QUIET_STREAK_PREFIX = 'Quiet' as const

/** Status when expression is due inside the midday window. */
export const CREW_BRIEFING_STATUS_DUE_NOW =
  'Due' as const

/** Status when next run cannot be projected for this expression. */
export const CREW_BRIEFING_STATUS_UNKNOWN =
  '—' as const

/**
 * Consecutive on_change empty skips before midday auto-pauses the monitor.
 * Soft lifecycle — same cron; no sibling worker.
 */
export const CREW_BRIEFING_EMPTY_SKIP_AUTO_PAUSE_AFTER = 3 as const

/**
 * Your-crew Scheduled list chrome (F6 — ChatGPT Scheduled successor UX).
 * Single jsonb `briefing_schedule` → 0|1 row. Do not invent multi-schedule
 * storage here. Metro mirrors named exports in mobile briefing-schedule.ts.
 */
export const CREW_SCHEDULED_LIST_TITLE = 'Scheduled' as const

/** Supporting line under the Scheduled list heading. */
export const CREW_SCHEDULED_LIST_HINT =
  'When overnight briefs should land.' as const

/** Empty state when prefs have no briefing_schedule. */
export const CREW_SCHEDULED_LIST_EMPTY = 'No schedule yet' as const

/** CTA when the list is empty — opens the InterestBrief schedule panel. */
export const CREW_SCHEDULED_LIST_ADD_LABEL = 'Set a schedule' as const

/** Accessibility label for the empty-state add control. */
export const CREW_SCHEDULED_LIST_ADD_A11Y =
  'Set when your brief should run' as const

/** Row title fallback when monitorQuery is empty. */
/** Empty monitor row title — silence (anti-slop), no "Scheduled monitor" invent. */
export const CREW_SCHEDULED_LIST_UNTITLED = '' as const

/** Pause control — sets enabled=false while keeping expression + query. */
export const CREW_SCHEDULED_PAUSE_LABEL = 'Pause' as const

/** Resume control — sets enabled=true. */
export const CREW_SCHEDULED_RESUME_LABEL = 'Resume' as const

/** Edit control — opens InterestBrief schedule panel. */
export const CREW_SCHEDULED_EDIT_LABEL = 'Edit' as const

/** Delete control — clears briefing_schedule (null PUT). */
export const CREW_SCHEDULED_DELETE_LABEL = 'Delete' as const

export const CREW_SCHEDULED_PAUSE_A11Y = 'Pause' as const
export const CREW_SCHEDULED_RESUME_A11Y = 'Resume' as const
/** Resume after soft-lifecycle quiet-check auto-pause — prefer over bare Resume a11y. */
export const CREW_SCHEDULED_RESUME_AUTO_PAUSED_A11Y =
  'Resume' as const
export const CREW_SCHEDULED_EDIT_A11Y = 'Edit' as const
export const CREW_SCHEDULED_DELETE_A11Y = 'Delete' as const

/** Confirm before clearing a scheduled monitor (HIG destructive). */
export const CREW_SCHEDULED_DELETE_CONFIRM_TITLE =
  'Delete?' as const
export const CREW_SCHEDULED_DELETE_CONFIRM_BODY =
  '' as const
export const CREW_SCHEDULED_DELETE_CONFIRM_ACTION = 'Delete' as const
export const CREW_SCHEDULED_DELETE_CONFIRM_CANCEL = 'Cancel' as const

/** Region id — Scheduled list landmark. */
export const CREW_SCHEDULED_LIST_REGION_ID = 'crew-scheduled-list' as const

/**
 * Closed set of Your-crew Scheduled list chrome (web ↔ mobile).
 * Prefer named exports at call sites; MIRROR is for drift audits.
 */
export const CREW_SCHEDULED_LIST_MIRROR = {
  CREW_SCHEDULED_LIST_TITLE,
  CREW_SCHEDULED_LIST_HINT,
  CREW_SCHEDULED_LIST_EMPTY,
  CREW_SCHEDULED_LIST_ADD_LABEL,
  CREW_SCHEDULED_LIST_ADD_A11Y,
  CREW_SCHEDULED_LIST_UNTITLED,
  CREW_SCHEDULED_PAUSE_LABEL,
  CREW_SCHEDULED_RESUME_LABEL,
  CREW_SCHEDULED_EDIT_LABEL,
  CREW_SCHEDULED_DELETE_LABEL,
  CREW_SCHEDULED_PAUSE_A11Y,
  CREW_SCHEDULED_RESUME_A11Y,
  CREW_SCHEDULED_RESUME_AUTO_PAUSED_A11Y,
  CREW_SCHEDULED_EDIT_A11Y,
  CREW_SCHEDULED_DELETE_A11Y,
  CREW_SCHEDULED_LIST_REGION_ID
} as const

/**
 * Closed set of CrewInterestBrief scheduled-monitor chrome (web ↔ mobile).
 * Prefer named exports at call sites; use this object for parity audits.
 * Metro cannot import web/ — mobile mirrors in briefing-schedule.ts.
 * Bun: `__tests__/lib/crew-briefing-schedule-copy-parity.test.ts`.
 */
export const CREW_BRIEFING_CHROME_MIRROR = {
  CREW_BRIEFING_SECTION_LABEL,
  CREW_BRIEFING_SECTION_HIDE_LABEL,
  CREW_BRIEFING_ENABLE_LABEL,
  CREW_BRIEFING_SCHEDULE_FIELD_LABEL,
  CREW_BRIEFING_MONITOR_FIELD_LABEL,
  CREW_BRIEFING_MONITOR_MODE_FIELD_LABEL,
  CREW_BRIEFING_MONITOR_MODE_CADENCE_LABEL,
  CREW_BRIEFING_MONITOR_MODE_ON_CHANGE_LABEL,
  CREW_BRIEFING_MONITOR_MODE_HINT,
  CREW_BRIEFING_SAVE_LABEL,
  CREW_BRIEFING_SAVE_A11Y_LABEL,
  CREW_BRIEFING_SCHEDULE_DEFAULT,
  CREW_BRIEFING_SCHEDULE_PLACEHOLDER,
  CREW_BRIEFING_MONITOR_PLACEHOLDER,
  CREW_BRIEFING_SCHEDULE_FIELD_NAME,
  CREW_BRIEFING_MONITOR_FIELD_NAME,
  CREW_BRIEFING_MONITOR_MODE_FIELD_NAME,
  CREW_BRIEFING_MONITOR_MODE_GROUP_ID,
  CREW_BRIEFING_MONITOR_MODE_HINT_ID,
  CREW_BRIEFING_SCHEDULE_PANEL_ID,
  CREW_BRIEFING_STATUS_ID,
  CREW_BRIEFING_NEXT_RUN_PREFIX,
  CREW_BRIEFING_STATUS_PAUSED,
  CREW_BRIEFING_STATUS_AUTO_PAUSED,
  CREW_BRIEFING_QUIET_STREAK_PREFIX,
  CREW_BRIEFING_STATUS_DUE_NOW,
  CREW_BRIEFING_STATUS_UNKNOWN
} as const

/**
 * Scheduled-monitor toast / Alert soft-copy — lockstep with mobile
 * briefing-schedule.ts. Do not fork literals in CrewInterestBrief.
 */
export const CREW_SCHEDULE_SAVE_ERROR =
  'Could not' as const
export const CREW_SCHEDULE_SAVE_SUCCESS = 'Saved' as const
export const CREW_SCHEDULE_CLEARED = 'Cleared' as const
/** Alert / toast title when validateCrewBriefingSchedule fails. */
export const CREW_SCHEDULE_INCOMPLETE_TITLE =
  '—' as const

/**
 * Closed set of scheduled-monitor toast / Alert soft-copy (web ↔ mobile).
 * Prefer named CREW_SCHEDULE_* at call sites; MIRROR is for drift audits.
 * Metro cannot import web/ — mobile mirrors in briefing-schedule.ts.
 * Bun: `__tests__/lib/crew-briefing-schedule-copy-parity.test.ts` +
 * `__tests__/lib/crew-love-whisper-toast-copy-parity.test.ts`.
 */
export const CREW_SCHEDULE_TOAST_MIRROR = {
  CREW_SCHEDULE_SAVE_ERROR,
  CREW_SCHEDULE_SAVE_SUCCESS,
  CREW_SCHEDULE_CLEARED,
  CREW_SCHEDULE_INCOMPLETE_TITLE
} as const

/**
 * validateCrewBriefingSchedule error bodies — toast description / Alert
 * message under CREW_SCHEDULE_INCOMPLETE_TITLE. Keep identical named
 * exports in mobile/lib/crew/briefing-schedule.ts.
 */
/** Shared validate body — Invalid schedule / expression (no coaching invent). */
export const CREW_BRIEFING_VALIDATE_INVALID =
  'Invalid' as const

export const CREW_BRIEFING_VALIDATE_NOT_OBJECT =
  CREW_BRIEFING_VALIDATE_INVALID

export const CREW_BRIEFING_VALIDATE_REQUIRES_SCHEDULE =
  'Schedule' as const
export const CREW_BRIEFING_VALIDATE_BAD_EXPRESSION =
  CREW_BRIEFING_VALIDATE_INVALID
export const CREW_BRIEFING_VALIDATE_REQUIRES_MONITOR = 'Topic' as const

/**
 * Closed set of validateCrewBriefingSchedule error bodies mirrored on mobile
 * as named CREW_BRIEFING_VALIDATE_* only. Prefer named constants at call sites;
 * use this object for parity / drift audits (not a second copy source).
 * Metro cannot import web/ — mobile must not fork CREW_BRIEFING_VALIDATE_MIRROR.
 * Bun: `__tests__/lib/crew-briefing-schedule-copy-parity.test.ts` +
 * `__tests__/lib/crew-feed-barrel-parity.test.ts`.
 */
export const CREW_BRIEFING_VALIDATE_MIRROR = {
  CREW_BRIEFING_VALIDATE_INVALID,
  CREW_BRIEFING_VALIDATE_NOT_OBJECT,
  CREW_BRIEFING_VALIDATE_REQUIRES_SCHEDULE,
  CREW_BRIEFING_VALIDATE_BAD_EXPRESSION,
  CREW_BRIEFING_VALIDATE_REQUIRES_MONITOR
} as const

const CRON_FIELD =
  /^(?:\*(?:\/\d+)?|\d+(?:-\d+)?(?:,\d+(?:-\d+)?)*(?:\/\d+)?)$/

const RRULE_FREQ = /^(?:HOURLY|DAILY|WEEKLY)$/i

export type CrewBriefingSchedule = {
  enabled: boolean
  /**
   * RRULE-lite (`FREQ=DAILY;BYHOUR=7;BYMINUTE=0`) or 5-field cron
   * (`0 7 * * *`). Empty only allowed when `enabled` is false.
   */
  schedule: string
  /** Interest monitor query — “monitor web for X”. */
  monitorQuery: string
  /**
   * `cadence` (default) always regenerates on due; `on_change` skips empty
   * / no-delta interest-lane inserts (feed-refresh delta-skip helper).
   */
  monitorMode: CrewBriefingMonitorMode
  /**
   * Consecutive on_change empty / no-delta skips since last delta persist.
   * Soft lifecycle counter — reset on has-delta or manual resume.
   */
  emptySkipCount: number
  /**
   * True when `enabled` was flipped off by empty-skip auto-pause
   * (not a manual Pause). Cleared on resume / delta persist.
   */
  autoPaused: boolean
  /** ISO timestamp of last empty skip; null when none / reset. */
  lastEmptySkipAt: string | null
}

export type CardTypePreferencesWithBriefing = {
  chat_follow_up?: boolean
  project_reminder?: boolean
  insight?: boolean
  calendar_context?: boolean
  memory_highlight?: boolean
  task_suggestion?: boolean
  meta?: {
    interest_brief?: string
    briefing_schedule?: CrewBriefingSchedule
    [key: string]: unknown
  }
  [key: string]: unknown
}

export type BriefingScheduleValidation =
  | { ok: true; value: CrewBriefingSchedule }
  | { ok: false; error: string }

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function isFiveFieldCron(schedule: string): boolean {
  const parts = schedule.trim().split(/\s+/)
  if (parts.length !== 5) return false
  return parts.every(part => CRON_FIELD.test(part))
}

function isRruleLite(schedule: string): boolean {
  const trimmed = schedule.trim()
  if (!trimmed.toUpperCase().includes('FREQ=')) return false

  const segments = trimmed.split(';').map(s => s.trim()).filter(Boolean)
  if (segments.length === 0) return false

  let sawFreq = false
  for (const segment of segments) {
    const eq = segment.indexOf('=')
    if (eq <= 0) return false
    const key = segment.slice(0, eq).toUpperCase()
    const raw = segment.slice(eq + 1).trim()
    if (!raw) return false

    if (key === 'FREQ') {
      if (!RRULE_FREQ.test(raw)) return false
      sawFreq = true
      continue
    }

    // Allowed RRULE-lite keys only — keep the spike surface small.
    if (
      key === 'INTERVAL' ||
      key === 'BYHOUR' ||
      key === 'BYMINUTE' ||
      key === 'BYDAY' ||
      key === 'COUNT'
    ) {
      if (!/^[A-Za-z0-9,+-]+$/.test(raw)) return false
      continue
    }

    // UNTIL=YYYYMMDD or UNTIL=YYYYMMDDTHHMMSSZ (inclusive end day).
    if (key === 'UNTIL') {
      if (!parseRruleUntilYmd(raw)) return false
      continue
    }

    // DTSTART — same date forms as UNTIL; anchors INTERVAL>1 when present.
    if (key === 'DTSTART') {
      if (!parseRruleDtstartYmd(raw)) return false
      continue
    }

    return false
  }

  return sawFreq
}

/**
 * Parse RRULE UNTIL into local YYYY-MM-DD (date-only; time forms use the UTC
 * calendar day). Invalid → null.
 */
export function parseRruleUntilYmd(raw: string): string | null {
  const trimmed = raw.trim().toUpperCase()
  const match = /^(\d{4})(\d{2})(\d{2})(?:T\d{6}Z)?$/.exec(trimmed)
  if (!match) return null
  const year = Number.parseInt(match[1]!, 10)
  const month = Number.parseInt(match[2]!, 10)
  const day = Number.parseInt(match[3]!, 10)
  if (
    !Number.isFinite(year) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null
  }
  // Reject impossible calendar days (e.g. 20260231) via UTC Date round-trip.
  const probe = new Date(Date.UTC(year, month - 1, day))
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null
  }
  return `${match[1]}-${match[2]}-${match[3]}`
}

/**
 * Parse RRULE DTSTART into local YYYY-MM-DD (same forms as UNTIL).
 * Used as INTERVAL>1 anchor when present. Invalid → null.
 */
export function parseRruleDtstartYmd(raw: string): string | null {
  return parseRruleUntilYmd(raw)
}

/** True when schedule is RRULE-lite or a 5-field cron expression. */
export function isValidBriefingScheduleExpression(schedule: string): boolean {
  const trimmed = schedule.trim()
  if (!trimmed || trimmed.length > CREW_BRIEFING_SCHEDULE_MAX) return false
  return isFiveFieldCron(trimmed) || isRruleLite(trimmed)
}

/**
 * Validate + normalize a Crew briefing schedule preference.
 * Disabled schedules may omit a real expression; enabled ones must pass.
 */
export function validateCrewBriefingSchedule(
  input: unknown
): BriefingScheduleValidation {
  if (!isRecord(input)) {
    return { ok: false, error: CREW_BRIEFING_VALIDATE_NOT_OBJECT }
  }

  const enabled = Boolean(input.enabled)
  const scheduleRaw =
    typeof input.schedule === 'string'
      ? input.schedule
      : typeof input.rrule === 'string'
        ? input.rrule
        : typeof input.cron === 'string'
          ? input.cron
          : ''
  const monitorRaw =
    typeof input.monitorQuery === 'string'
      ? input.monitorQuery
      : typeof input.monitor_query === 'string'
        ? input.monitor_query
        : typeof input.query === 'string'
          ? input.query
          : ''

  const schedule = scheduleRaw.trim().slice(0, CREW_BRIEFING_SCHEDULE_MAX)
  const monitorQuery = monitorRaw
    .trim()
    .slice(0, CREW_BRIEFING_MONITOR_QUERY_MAX)
  const monitorModeRaw =
    typeof input.monitorMode === 'string'
      ? input.monitorMode
      : typeof input.monitor_mode === 'string'
        ? input.monitor_mode
        : CREW_BRIEFING_MONITOR_MODE_DEFAULT
  const monitorMode = normalizeCrewBriefingMonitorMode(monitorModeRaw)
  const emptySkipCount = normalizeCrewBriefingEmptySkipCount(
    input.emptySkipCount != null
      ? input.emptySkipCount
      : input.empty_skip_count
  )
  const autoPaused =
    input.autoPaused === true ||
    input.auto_paused === true ||
    (typeof input.autoPaused === 'string' &&
      input.autoPaused.trim().toLowerCase() === 'true') ||
    (typeof input.auto_paused === 'string' &&
      input.auto_paused.trim().toLowerCase() === 'true')
  const lastEmptySkipAt = normalizeCrewBriefingLastEmptySkipAt(
    input.lastEmptySkipAt != null
      ? input.lastEmptySkipAt
      : input.last_empty_skip_at
  )

  if (enabled) {
    if (!schedule) {
      return {
        ok: false,
        error: CREW_BRIEFING_VALIDATE_REQUIRES_SCHEDULE
      }
    }
    if (!isValidBriefingScheduleExpression(schedule)) {
      return {
        ok: false,
        error: CREW_BRIEFING_VALIDATE_BAD_EXPRESSION
      }
    }
    if (!monitorQuery) {
      return {
        ok: false,
        error: CREW_BRIEFING_VALIDATE_REQUIRES_MONITOR
      }
    }
  }

  return {
    ok: true,
    value: {
      enabled,
      schedule,
      monitorQuery,
      monitorMode,
      emptySkipCount,
      autoPaused,
      lastEmptySkipAt
    }
  }
}

/** Normalize unknown prefs input into a schedule or null when absent/invalid. */
export function normalizeCrewBriefingSchedule(
  input: unknown
): CrewBriefingSchedule | null {
  if (input == null) return null
  const result = validateCrewBriefingSchedule(input)
  return result.ok ? result.value : null
}

/**
 * Honest Scheduled list projection from singular prefs.
 * Returns [] when absent / empty draft; otherwise a one-element list.
 * Do not invent multi-schedule jsonb here.
 */
export function listCrewBriefingSchedules(
  schedule: CrewBriefingSchedule | null | undefined
): CrewBriefingSchedule[] {
  if (!schedule) return []
  const hasExpression = Boolean(schedule.schedule?.trim())
  const hasQuery = Boolean(schedule.monitorQuery?.trim())
  if (!schedule.enabled && !hasExpression && !hasQuery) return []
  return [schedule]
}

/** Row title — monitorQuery, else expression, else untitled chrome. */
export function crewBriefingScheduleRowTitle(
  schedule: Pick<CrewBriefingSchedule, 'monitorQuery' | 'schedule'>
): string {
  const query =
    typeof schedule.monitorQuery === 'string' ? schedule.monitorQuery.trim() : ''
  if (query) return query
  const expression =
    typeof schedule.schedule === 'string' ? schedule.schedule.trim() : ''
  if (expression) return expression
  return CREW_SCHEDULED_LIST_UNTITLED
}

/** Clone schedule with enabled flipped (pause / resume). */
export function withCrewBriefingScheduleEnabled(
  schedule: CrewBriefingSchedule,
  enabled: boolean
): CrewBriefingSchedule {
  const nextEnabled = Boolean(enabled)
  if (nextEnabled) {
    // Resume clears soft-lifecycle idle counters so monitoring restarts clean.
    return {
      ...schedule,
      enabled: true,
      emptySkipCount: 0,
      autoPaused: false,
      lastEmptySkipAt: null
    }
  }
  // Manual pause — keep counters for observability but clear autoPaused flag
  // so status copy reads as a user pause, not quiet-check auto-pause.
  return {
    ...schedule,
    enabled: false,
    autoPaused: false
  }
}

/**
 * Clamp consecutive empty-skip counter (jsonb / form input).
 * Non-finite / missing → 0. Soft cap 99.
 */
export function normalizeCrewBriefingEmptySkipCount(input: unknown): number {
  if (typeof input !== 'number' || !Number.isFinite(input)) return 0
  return Math.min(99, Math.max(0, Math.trunc(input)))
}

/**
 * Normalize last-empty-skip ISO timestamp. Invalid / empty → null.
 */
export function normalizeCrewBriefingLastEmptySkipAt(
  input: unknown
): string | null {
  if (typeof input !== 'string') return null
  const trimmed = input.trim()
  if (!trimmed) return null
  const ms = Date.parse(trimmed)
  if (!Number.isFinite(ms)) return null
  return new Date(ms).toISOString()
}

export type CrewBriefingOnChangeEmptySkipLifecycleResult = {
  next: CrewBriefingSchedule
  changed: boolean
  didAutoPause: boolean
  emptySkipCount: number
}

/**
 * Soft lifecycle for on_change monitors after a midday research tick.
 *
 * - cadence / missing → no-op (prod wipe-on-empty unchanged)
 * - on_change + has delta (`skippedEmpty=false`) → reset counter
 * - on_change + empty skip → increment; at threshold → enabled=false + autoPaused
 *
 * Pure — PulseAgent persists `next` when `changed`. Same midday cron; no
 * sibling worker.
 */
export function applyCrewBriefingOnChangeEmptySkipLifecycle(
  schedule: CrewBriefingSchedule,
  args: {
    skippedEmpty: boolean
    nowIso?: string
    autoPauseAfter?: number
  }
): CrewBriefingOnChangeEmptySkipLifecycleResult {
  if (
    normalizeCrewBriefingMonitorMode(schedule.monitorMode) !==
    CREW_BRIEFING_MONITOR_MODE_ON_CHANGE
  ) {
    return {
      next: schedule,
      changed: false,
      didAutoPause: false,
      emptySkipCount: normalizeCrewBriefingEmptySkipCount(schedule.emptySkipCount)
    }
  }

  const threshold = clampInt(
    args.autoPauseAfter ?? CREW_BRIEFING_EMPTY_SKIP_AUTO_PAUSE_AFTER,
    1,
    99
  )
  const prevCount = normalizeCrewBriefingEmptySkipCount(schedule.emptySkipCount)

  if (!args.skippedEmpty) {
    const next: CrewBriefingSchedule = {
      ...schedule,
      emptySkipCount: 0,
      autoPaused: false,
      lastEmptySkipAt: null
    }
    const changed =
      prevCount !== 0 ||
      Boolean(schedule.autoPaused) ||
      Boolean(schedule.lastEmptySkipAt)
    return {
      next: changed ? next : schedule,
      changed,
      didAutoPause: false,
      emptySkipCount: 0
    }
  }

  const emptySkipCount = Math.min(99, prevCount + 1)
  const didAutoPause = emptySkipCount >= threshold
  const nowIso =
    normalizeCrewBriefingLastEmptySkipAt(args.nowIso) ||
    new Date().toISOString()

  const next: CrewBriefingSchedule = {
    ...schedule,
    emptySkipCount,
    lastEmptySkipAt: nowIso,
    ...(didAutoPause
      ? { enabled: false, autoPaused: true }
      : { autoPaused: false })
  }

  return {
    next,
    changed: true,
    didAutoPause,
    emptySkipCount
  }
}

/** True when status / Scheduled list should show auto-paused copy. */
export function isCrewBriefingScheduleAutoPaused(
  schedule:
    | Pick<CrewBriefingSchedule, 'enabled' | 'autoPaused' | 'emptySkipCount'>
    | null
    | undefined
): boolean {
  if (!schedule || schedule.enabled) return false
  if (schedule.autoPaused) return true
  return (
    normalizeCrewBriefingEmptySkipCount(schedule.emptySkipCount) >=
    CREW_BRIEFING_EMPTY_SKIP_AUTO_PAUSE_AFTER
  )
}

/**
 * Accessible name for the Pause/Resume control.
 * Auto-paused rows use CREW_SCHEDULED_RESUME_AUTO_PAUSED_A11Y.
 */
export function crewScheduledPauseResumeA11y(
  schedule:
    | Pick<CrewBriefingSchedule, 'enabled' | 'autoPaused' | 'emptySkipCount'>
    | null
    | undefined
): string {
  if (!schedule) return CREW_SCHEDULED_RESUME_A11Y
  if (schedule.enabled) return CREW_SCHEDULED_PAUSE_A11Y
  if (isCrewBriefingScheduleAutoPaused(schedule)) {
    return CREW_SCHEDULED_RESUME_AUTO_PAUSED_A11Y
  }
  return CREW_SCHEDULED_RESUME_A11Y
}

/**
 * Quiet-check streak fragment for on_change monitors (`Quiet checks · 2 of 3`).
 * Null when cadence / manual pause / zero streak — do not invent progress.
 * Auto-paused rows still surface the streak that tripped the soft lifecycle.
 */
export function formatCrewBriefingQuietStreakStatus(
  schedule:
    | Pick<
        CrewBriefingSchedule,
        'enabled' | 'monitorMode' | 'emptySkipCount' | 'autoPaused'
      >
    | null
    | undefined,
  opts?: { autoPauseAfter?: number }
): string | null {
  if (!schedule) return null
  const showWhileAutoPaused = isCrewBriefingScheduleAutoPaused(schedule)
  if (!schedule.enabled && !showWhileAutoPaused) return null
  if (
    normalizeCrewBriefingMonitorMode(schedule.monitorMode) !==
    CREW_BRIEFING_MONITOR_MODE_ON_CHANGE
  ) {
    return null
  }
  const count = normalizeCrewBriefingEmptySkipCount(schedule.emptySkipCount)
  if (count <= 0) return null
  const threshold = clampInt(
    opts?.autoPauseAfter ?? CREW_BRIEFING_EMPTY_SKIP_AUTO_PAUSE_AFTER,
    1,
    99
  )
  return `${CREW_BRIEFING_QUIET_STREAK_PREFIX} · ${count} of ${threshold}`
}

/**
 * Empty-edition soft hint when the on_change monitor auto-paused after quiet
 * checks. Null when not auto-paused — do not invent schedule copy.
 * Returns CREW_BRIEFING_STATUS_AUTO_PAUSED only — Quiet streak stays on the
 * Scheduled row (formatCrewBriefingScheduleStatus); do not duplicate N of M.
 */
export function resolveCrewFeedEmptyScheduleHint(
  schedule:
    | Pick<
        CrewBriefingSchedule,
        'enabled' | 'monitorMode' | 'emptySkipCount' | 'autoPaused'
      >
    | null
    | undefined
): string | null {
  if (!isCrewBriefingScheduleAutoPaused(schedule)) return null
  return CREW_BRIEFING_STATUS_AUTO_PAUSED
}

/** Read briefing_schedule from card_type_preferences jsonb. */
export function extractCrewBriefingSchedule(
  cardTypePreferences: unknown
): CrewBriefingSchedule | null {
  if (!isRecord(cardTypePreferences)) return null
  const meta = cardTypePreferences.meta
  if (!isRecord(meta)) return null
  return normalizeCrewBriefingSchedule(meta.briefing_schedule)
}

/**
 * Trimmed monitorQuery when the schedule is enabled and has a query.
 * Empty / disabled / missing → `""`. Cron / midday research use this —
 * do not re-trim monitor text in pulse-agent or cron.
 */
export function extractEnabledMonitorQuery(
  schedule:
    | Pick<CrewBriefingSchedule, 'enabled' | 'monitorQuery'>
    | null
    | undefined
): string {
  if (!schedule?.enabled) return ''
  const raw =
    typeof schedule.monitorQuery === 'string' ? schedule.monitorQuery : ''
  return raw.trim().slice(0, CREW_BRIEFING_MONITOR_QUERY_MAX)
}

/**
 * Read monitorMode from a schedule (camel or snake). Missing → cadence.
 * Prefer this over forking defaults in feed-refresh / pulse-agent.
 */
export function extractCrewBriefingMonitorMode(
  schedule:
    | Pick<CrewBriefingSchedule, 'monitorMode'>
    | { monitorMode?: unknown; monitor_mode?: unknown }
    | null
    | undefined
): CrewBriefingMonitorMode {
  if (!schedule) return CREW_BRIEFING_MONITOR_MODE_DEFAULT
  const record = schedule as {
    monitorMode?: unknown
    monitor_mode?: unknown
  }
  const raw =
    record.monitorMode != null ? record.monitorMode : record.monitor_mode
  return normalizeCrewBriefingMonitorMode(raw)
}

/**
 * Merge a briefing schedule into card_type_preferences without dropping
 * toggles or interest_brief. Pass null to clear the stored schedule.
 */
export function mergeCrewBriefingSchedule(
  existing: unknown,
  schedule: CrewBriefingSchedule | null
): CardTypePreferencesWithBriefing {
  const base: CardTypePreferencesWithBriefing = isRecord(existing)
    ? { ...existing }
    : {}
  const priorMeta = isRecord(base.meta) ? { ...base.meta } : {}

  if (schedule == null) {
    delete priorMeta.briefing_schedule
    if (Object.keys(priorMeta).length === 0) {
      delete base.meta
    } else {
      base.meta = priorMeta as CardTypePreferencesWithBriefing['meta']
    }
    return base
  }

  const validated = validateCrewBriefingSchedule(schedule)
  if (!validated.ok) {
    // Keep prior schedule on invalid merge input — callers should validate first.
    return base
  }

  base.meta = {
    ...priorMeta,
    briefing_schedule: validated.value
  }
  return base
}

/**
 * Default due window for F6 schedule evaluation in the Pulse cron.
 * Matches morning generation window — cron ticks should catch the slot
 * without holding “due” for a full hour.
 */
export const CREW_BRIEFING_SCHEDULE_DUE_WINDOW_MINUTES = 15 as const

export type CrewBriefingScheduleDueClock = {
  localHour: number
  localMinute?: number
  /**
   * JS weekday: 0 = Sunday … 6 = Saturday.
   * Required for weekday-scoped cron / RRULE BYDAY; omitted → any day.
   */
  localWeekday?: number
  /**
   * Local calendar date `YYYY-MM-DD` for INTERVAL (>1) + UNTIL gates.
   * Omitted → INTERVAL>1 / UNTIL expressions stay not-due (safe).
   */
  localDateYmd?: string
  /** Override default due window length in minutes. */
  windowMinutes?: number
}

const RRULE_BYDAY_TO_JS: Record<string, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6
}

/** Intl weekday short → JS 0=Sun…6=Sat (en-US formatToParts). */
const WEEKDAY_SHORT_TO_JS: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
}

function clampInt(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, Math.trunc(value)))
}

/**
 * Resolve the device / browser IANA time zone for schedule status chrome.
 * Empty / unavailable → '' so callers fall through to UTC via due-clock.
 * Prefer this over re-inlining `Intl.DateTimeFormat().resolvedOptions()`.
 * Keep identical in mobile/lib/crew/briefing-schedule.ts.
 */
export function resolveCrewLocalIanaTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof zone === 'string' ? zone.trim() : ''
  } catch {
    return ''
  }
}

/**
 * Build a due-eval clock from an instant + IANA zone via Intl.
 * DST-safe: wall-clock hour/minute/weekday come from the zone’s rules
 * (spring-forward gap / fall-back overlap resolve to the local parts Intl
 * reports). Invalid / empty zone → UTC. Prefer this over forking Luxon
 * weekday conversion in cron routes.
 */
export function resolveCrewBriefingScheduleDueClock(
  now: Date,
  timeZone?: string | null
): CrewBriefingScheduleDueClock {
  const zone = typeof timeZone === 'string' ? timeZone.trim() : ''
  const resolvedZone = zone || 'UTC'

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: resolvedZone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(now)

    let hour = 0
    let minute = 0
    let weekday: number | undefined
    let year = ''
    let month = ''
    let day = ''
    for (const part of parts) {
      if (part.type === 'hour') {
        hour = Number.parseInt(part.value, 10)
      } else if (part.type === 'minute') {
        minute = Number.parseInt(part.value, 10)
      } else if (part.type === 'weekday') {
        weekday = WEEKDAY_SHORT_TO_JS[part.value]
      } else if (part.type === 'year') {
        year = part.value.padStart(4, '0')
      } else if (part.type === 'month') {
        month = part.value.padStart(2, '0')
      } else if (part.type === 'day') {
        day = part.value.padStart(2, '0')
      }
    }

    const localDateYmd =
      year && month && day ? `${year}-${month}-${day}` : undefined

    return {
      localHour: clampInt(Number.isFinite(hour) ? hour : 0, 0, 23),
      localMinute: clampInt(Number.isFinite(minute) ? minute : 0, 0, 59),
      ...(weekday !== undefined ? { localWeekday: weekday } : {}),
      ...(localDateYmd ? { localDateYmd } : {})
    }
  } catch {
    // Invalid IANA zone — fall back to UTC parts.
    if (resolvedZone !== 'UTC') {
      return resolveCrewBriefingScheduleDueClock(now, 'UTC')
    }
    const y = now.getUTCFullYear()
    const m = String(now.getUTCMonth() + 1).padStart(2, '0')
    const d = String(now.getUTCDate()).padStart(2, '0')
    return {
      localHour: now.getUTCHours(),
      localMinute: now.getUTCMinutes(),
      localWeekday: now.getUTCDay(),
      localDateYmd: `${y}-${m}-${d}`
    }
  }
}

/**
 * Match a single cron field against a numeric value (star, n, n-m,
 * star/step, n-m/step, comma lists). Keep parsers here — do not fork in cron.
 */
function matchesCronNumericField(
  field: string,
  value: number,
  min: number,
  max: number
): boolean {
  const trimmed = field.trim()
  if (!trimmed) return false
  if (trimmed === '*') return true

  for (const part of trimmed.split(',')) {
    const token = part.trim()
    if (!token) continue

    const stepMatch = /^(\*|\d+(?:-\d+)?)(?:\/(\d+))?$/.exec(token)
    if (!stepMatch) continue

    const rangeOrStar = stepMatch[1]
    const stepRaw = stepMatch[2]
    const step = stepRaw ? Number.parseInt(stepRaw, 10) : 1
    if (!Number.isFinite(step) || step < 1) continue

    let start = min
    let end = max
    if (rangeOrStar !== '*') {
      const dash = rangeOrStar.indexOf('-')
      if (dash < 0) {
        start = Number.parseInt(rangeOrStar, 10)
        end = start
      } else {
        start = Number.parseInt(rangeOrStar.slice(0, dash), 10)
        end = Number.parseInt(rangeOrStar.slice(dash + 1), 10)
      }
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue
      if (start > end) continue
    }

    if (value < start || value > end) continue
    if ((value - start) % step === 0) return true
  }

  return false
}

/**
 * Cron day-of-week: 0 and 7 = Sunday, 1 = Monday … 6 = Saturday.
 * `localWeekday` is JS 0–6. Expands lists/ranges then normalizes 7→0.
 */
function matchesCronDayOfWeek(field: string, localWeekday: number): boolean {
  const trimmed = field.trim()
  if (trimmed === '*') return true
  const day = clampInt(localWeekday, 0, 6)

  for (const part of trimmed.split(',')) {
    const token = part.trim()
    if (!token) continue

    const stepMatch = /^(\*|\d+(?:-\d+)?)(?:\/(\d+))?$/.exec(token)
    if (!stepMatch) continue

    const rangeOrStar = stepMatch[1]!
    const stepRaw = stepMatch[2]
    const step = stepRaw ? Number.parseInt(stepRaw, 10) : 1
    if (!Number.isFinite(step) || step < 1) continue

    let start = 0
    let end = 7
    if (rangeOrStar !== '*') {
      const dash = rangeOrStar.indexOf('-')
      if (dash < 0) {
        start = Number.parseInt(rangeOrStar, 10)
        end = start
      } else {
        start = Number.parseInt(rangeOrStar.slice(0, dash), 10)
        end = Number.parseInt(rangeOrStar.slice(dash + 1), 10)
      }
      if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) {
        continue
      }
    }

    for (let raw = start; raw <= end; raw++) {
      if ((raw - start) % step !== 0) continue
      const normalized = raw === 7 ? 0 : raw
      if (normalized === day) return true
    }
  }

  return false
}

function isWithinMinuteWindow(args: {
  currentMinutes: number
  targetMinutes: number
  windowMinutes: number
}): boolean {
  const windowMinutes = Math.max(1, Math.round(args.windowMinutes))
  let current = args.currentMinutes
  const start = args.targetMinutes
  if (current < start && start + windowMinutes > 24 * 60) {
    current += 24 * 60
  }
  return current >= start && current < start + windowMinutes
}

function parseRruleLiteParts(
  schedule: string
): {
  freq: string
  interval: number
  byHour: number[] | null
  byMinute: number[] | null
  byDay: number[] | null
  untilYmd: string | null
  dtstartYmd: string | null
} | null {
  const trimmed = schedule.trim()
  if (!trimmed.toUpperCase().includes('FREQ=')) return null

  let freq = ''
  let interval = 1
  let byHour: number[] | null = null
  let byMinute: number[] | null = null
  let byDay: number[] | null = null
  let untilYmd: string | null = null
  let dtstartYmd: string | null = null

  for (const segment of trimmed.split(';').map(s => s.trim()).filter(Boolean)) {
    const eq = segment.indexOf('=')
    if (eq <= 0) return null
    const key = segment.slice(0, eq).toUpperCase()
    const raw = segment.slice(eq + 1).trim()
    if (!raw) return null

    if (key === 'FREQ') {
      freq = raw.toUpperCase()
      continue
    }
    if (key === 'INTERVAL') {
      const n = Number.parseInt(raw, 10)
      if (!Number.isFinite(n) || n < 1) return null
      interval = n
      continue
    }
    if (key === 'BYHOUR') {
      byHour = raw.split(',').map(p => Number.parseInt(p.trim(), 10))
      if (byHour.some(n => !Number.isFinite(n) || n < 0 || n > 23)) return null
      continue
    }
    if (key === 'BYMINUTE') {
      byMinute = raw.split(',').map(p => Number.parseInt(p.trim(), 10))
      if (byMinute.some(n => !Number.isFinite(n) || n < 0 || n > 59)) return null
      continue
    }
    if (key === 'BYDAY') {
      byDay = []
      for (const token of raw.split(',')) {
        const day = RRULE_BYDAY_TO_JS[token.trim().toUpperCase()]
        if (day === undefined) return null
        byDay.push(day)
      }
      continue
    }
    if (key === 'UNTIL') {
      untilYmd = parseRruleUntilYmd(raw)
      if (!untilYmd) return null
      continue
    }
    if (key === 'DTSTART') {
      dtstartYmd = parseRruleDtstartYmd(raw)
      if (!dtstartYmd) return null
      continue
    }
    if (key === 'COUNT') {
      // COUNT is accepted by the validator but ignored for due-window checks.
      continue
    }
    return null
  }

  if (!freq) return null
  return { freq, interval, byHour, byMinute, byDay, untilYmd, dtstartYmd }
}

/** Days since Unix epoch (UTC) for a local YYYY-MM-DD calendar date. */
function daysSinceUnixEpochUtc(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(p => Number.parseInt(p, 10))
  const ms = Date.UTC(y!, m! - 1, d!)
  return Math.floor(ms / 86_400_000)
}

/**
 * Weeks since Monday 1970-01-05 UTC (day 0 of week 0). Used as the RRULE-lite
 * WEEKLY INTERVAL anchor when DTSTART is absent.
 */
function weeksSinceMondayEpochUtc(ymd: string): number {
  // 1970-01-05 was a Monday; floor((days - 4) / 7) from Unix epoch.
  return Math.floor((daysSinceUnixEpochUtc(ymd) - 4) / 7)
}

/**
 * JS weekday gate for RRULE BYDAY (0=Sun…6=Sat).
 * Empty / null byDay → true. Missing localWeekday when BYDAY set → false.
 */
export function matchesCrewRruleByDay(
  byDay: readonly number[] | null | undefined,
  localWeekday: number | undefined
): boolean {
  if (!byDay || byDay.length === 0) return true
  if (localWeekday === undefined) return false
  return byDay.includes(clampInt(localWeekday, 0, 6))
}

/**
 * INTERVAL gate for RRULE-lite.
 * HOURLY uses localHour; DAILY/WEEKLY need localDateYmd when interval > 1.
 * When `dtstartYmd` is present, DAILY/WEEKLY INTERVAL anchors off DTSTART
 * (day/week delta); otherwise Unix-epoch day / Monday-week anchors.
 * interval ≤ 1 → always true. Missing date when interval > 1 → false.
 * Before DTSTART (local < dtstart) → false.
 */
export function matchesCrewRruleInterval(args: {
  freq: string
  interval: number
  localHour?: number
  localDateYmd?: string | null
  dtstartYmd?: string | null
}): boolean {
  const interval = Math.max(1, Math.trunc(args.interval) || 1)
  if (interval <= 1) return true
  const freq = args.freq.toUpperCase()
  if (freq === 'HOURLY') {
    const hour = clampInt(args.localHour ?? 0, 0, 23)
    return hour % interval === 0
  }
  const ymd =
    typeof args.localDateYmd === 'string' ? args.localDateYmd.trim() : ''
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false
  const dtstart =
    typeof args.dtstartYmd === 'string' ? args.dtstartYmd.trim() : ''
  const hasDtstart = /^\d{4}-\d{2}-\d{2}$/.test(dtstart)
  if (hasDtstart) {
    const delta = daysSinceUnixEpochUtc(ymd) - daysSinceUnixEpochUtc(dtstart)
    if (delta < 0) return false
    if (freq === 'DAILY') return delta % interval === 0
    if (freq === 'WEEKLY') return Math.floor(delta / 7) % interval === 0
    return false
  }
  if (freq === 'DAILY') {
    return daysSinceUnixEpochUtc(ymd) % interval === 0
  }
  if (freq === 'WEEKLY') {
    return weeksSinceMondayEpochUtc(ymd) % interval === 0
  }
  return false
}

/**
 * UNTIL gate — inclusive on the until calendar day (YYYY-MM-DD).
 * null/empty until → active. Missing localDateYmd when until set → false.
 */
export function isCrewRruleActiveOnDate(
  untilYmd: string | null | undefined,
  localDateYmd: string | undefined
): boolean {
  if (!untilYmd) return true
  const local = typeof localDateYmd === 'string' ? localDateYmd.trim() : ''
  if (!/^\d{4}-\d{2}-\d{2}$/.test(local)) return false
  return local <= untilYmd
}

/**
 * True when a 5-field cron expression is due for the local clock.
 * Day-of-month / month must match (or `*`); DOW uses JS weekday when provided.
 */
export function isBriefingCronExpressionDue(
  schedule: string,
  clock: CrewBriefingScheduleDueClock
): boolean {
  const parts = schedule.trim().split(/\s+/)
  if (parts.length !== 5) return false
  if (!isFiveFieldCron(schedule.trim())) return false

  const [minuteField, hourField, domField, monthField, dowField] = parts as [
    string,
    string,
    string,
    string,
    string
  ]

  const hour = clampInt(clock.localHour, 0, 23)
  const minute = clampInt(clock.localMinute ?? 0, 0, 59)
  const windowMinutes =
    clock.windowMinutes ?? CREW_BRIEFING_SCHEDULE_DUE_WINDOW_MINUTES

  // Spike clock has no calendar date — DOM/month must be `*` (daily/weekly
  // schedules). Constrained DOM/month expressions stay not-due here.
  if (domField.trim() !== '*' || monthField.trim() !== '*') return false

  if (dowField.trim() !== '*') {
    if (clock.localWeekday === undefined) return false
    if (!matchesCronDayOfWeek(dowField, clock.localWeekday)) return false
  }

  // Expand matching hour/minute pairs; due when local clock is inside
  // [target, target + windowMinutes).
  const currentMinutes = hour * 60 + minute
  for (let h = 0; h < 24; h++) {
    if (!matchesCronNumericField(hourField, h, 0, 23)) continue
    for (let m = 0; m < 60; m++) {
      if (!matchesCronNumericField(minuteField, m, 0, 59)) continue
      if (
        isWithinMinuteWindow({
          currentMinutes,
          targetMinutes: h * 60 + m,
          windowMinutes
        })
      ) {
        return true
      }
    }
  }

  return false
}

/**
 * True when an RRULE-lite expression is due for the local clock.
 * HOURLY uses INTERVAL against localHour; DAILY/WEEKLY use BYHOUR/BYMINUTE
 * (default minute 0; missing BYHOUR → not due for WEEKLY/DAILY).
 * BYDAY / INTERVAL>1 / UNTIL use the standalone helpers below.
 */
export function isBriefingRruleExpressionDue(
  schedule: string,
  clock: CrewBriefingScheduleDueClock
): boolean {
  const parts = parseRruleLiteParts(schedule)
  if (!parts || !isRruleLite(schedule.trim())) return false

  const hour = clampInt(clock.localHour, 0, 23)
  const minute = clampInt(clock.localMinute ?? 0, 0, 59)
  const windowMinutes =
    clock.windowMinutes ?? CREW_BRIEFING_SCHEDULE_DUE_WINDOW_MINUTES
  const currentMinutes = hour * 60 + minute

  if (!matchesCrewRruleByDay(parts.byDay, clock.localWeekday)) return false
  if (!isCrewRruleActiveOnDate(parts.untilYmd, clock.localDateYmd)) {
    return false
  }
  if (
    !matchesCrewRruleInterval({
      freq: parts.freq,
      interval: parts.interval,
      localHour: hour,
      localDateYmd: clock.localDateYmd,
      dtstartYmd: parts.dtstartYmd
    })
  ) {
    return false
  }

  if (parts.freq === 'HOURLY') {
    const byMinute =
      parts.byMinute && parts.byMinute.length > 0 ? parts.byMinute : [0]
    for (const m of byMinute) {
      if (
        isWithinMinuteWindow({
          currentMinutes,
          targetMinutes: hour * 60 + m,
          windowMinutes
        })
      ) {
        return true
      }
    }
    return false
  }

  if (parts.freq === 'DAILY' || parts.freq === 'WEEKLY') {
    const byHour = parts.byHour
    if (!byHour || byHour.length === 0) return false
    const byMinute =
      parts.byMinute && parts.byMinute.length > 0 ? parts.byMinute : [0]
    for (const h of byHour) {
      for (const m of byMinute) {
        if (
          isWithinMinuteWindow({
            currentMinutes,
            targetMinutes: h * 60 + m,
            windowMinutes
          })
        ) {
          return true
        }
      }
    }
    return false
  }

  return false
}

/**
 * Evaluate whether a schedule expression is due now (cron or RRULE-lite).
 * Invalid / empty → false. Prefer this over forking parsers in cron routes.
 */
export function isBriefingScheduleExpressionDue(
  schedule: string,
  clock: CrewBriefingScheduleDueClock
): boolean {
  const trimmed = schedule.trim()
  if (!trimmed || !isValidBriefingScheduleExpression(trimmed)) return false
  if (isFiveFieldCron(trimmed)) {
    return isBriefingCronExpressionDue(trimmed, clock)
  }
  if (isRruleLite(trimmed)) {
    return isBriefingRruleExpressionDue(trimmed, clock)
  }
  return false
}

/**
 * F6 due gate for midday interest refresh.
 * Disabled / missing / empty schedule → false (caller falls back to midday
 * window). Enabled → true only when the expression is due for the local
 * clock. monitorQuery is intentionally ignored here — empty query still
 * yields due=true so midday can run whisper-only; use
 * `extractEnabledMonitorQuery` for the research prepend gate.
 */
export function isCrewBriefingScheduleDue(
  schedule:
    | Pick<CrewBriefingSchedule, 'enabled' | 'schedule'>
    | Pick<CrewBriefingSchedule, 'enabled' | 'schedule' | 'monitorQuery'>
    | null
    | undefined,
  clock: CrewBriefingScheduleDueClock
): boolean {
  if (!schedule?.enabled) return false
  if (!schedule.schedule?.trim()) return false
  return isBriefingScheduleExpressionDue(schedule.schedule, clock)
}

/**
 * Midday cron due flag — same as `isCrewBriefingScheduleDue`, but defaults
 * `windowMinutes` to `CREW_BRIEFING_SCHEDULE_DUE_WINDOW_MINUTES` so callers
 * (pulse-generation midday pass) do not fork the 15m window literal.
 */
export function resolveMiddayBriefingScheduleDue(
  schedule: Pick<CrewBriefingSchedule, 'enabled' | 'schedule'> | null | undefined,
  clock: CrewBriefingScheduleDueClock
): boolean {
  return isCrewBriefingScheduleDue(schedule, {
    ...clock,
    windowMinutes:
      clock.windowMinutes ?? CREW_BRIEFING_SCHEDULE_DUE_WINDOW_MINUTES
  })
}

/**
 * Thin cron due facade — resolves Intl clock (incl. localDateYmd for
 * INTERVAL / UNTIL) then `resolveMiddayBriefingScheduleDue`.
 *
 * Prefer this when a route already has `now` + IANA zone and only needs the
 * boolean. A future standalone RRULE worker (NOT SHIPPED) would compose this
 * with the env gate via `resolveScheduledBriefingWorkerShape` /
 * `resolveScheduledBriefingWorkerShapeFromInstant` — do not invent
 * a new cron process here. Midday Pulse still uses
 * `resolveCrewBriefingScheduleDueClock` + `runScheduledBriefingInterestRefresh`
 * so the same dueClock can feed localHour into the midday gate.
 * Persist stays env-gated elsewhere (`CREW_MIDDAY_INTEREST_REFRESH=1`).
 */
export function scheduledBriefingDueForCron(
  schedule:
    | Pick<CrewBriefingSchedule, 'enabled' | 'schedule'>
    | null
    | undefined,
  now: Date,
  timeZone?: string | null,
  opts?: { windowMinutes?: number }
): boolean {
  const clock = resolveCrewBriefingScheduleDueClock(now, timeZone)
  return resolveMiddayBriefingScheduleDue(schedule, {
    ...clock,
    ...(opts?.windowMinutes !== undefined
      ? { windowMinutes: opts.windowMinutes }
      : {})
  })
}

/** Default scan horizon for next-run preview (days). */
export const CREW_BRIEFING_NEXT_RUN_HORIZON_DAYS = 8 as const

/**
 * Walk forward from `now` (zone-aware) to the next minute where the
 * expression is due with a 1-minute window (exact fire minute). Caps at
 * `horizonDays` (default 8). Returns null when disabled / unparseable /
 * no hit in horizon. Prefer `formatCrewBriefingScheduleStatus` for UI copy.
 */
export function previewNextCrewBriefingRunAt(
  schedule:
    | Pick<CrewBriefingSchedule, 'enabled' | 'schedule'>
    | null
    | undefined,
  now: Date,
  timeZone?: string | null,
  opts?: { horizonDays?: number }
): Date | null {
  if (!schedule?.enabled) return null
  const trimmed =
    typeof schedule.schedule === 'string' ? schedule.schedule.trim() : ''
  if (!trimmed || !isValidBriefingScheduleExpression(trimmed)) return null

  const horizonDays = clampInt(
    opts?.horizonDays ?? CREW_BRIEFING_NEXT_RUN_HORIZON_DAYS,
    1,
    31
  )
  const zone = typeof timeZone === 'string' ? timeZone.trim() : ''
  const stepMs = 60_000
  const limit = horizonDays * 24 * 60

  for (let i = 0; i <= limit; i++) {
    const candidate = new Date(now.getTime() + i * stepMs)
    const clock = resolveCrewBriefingScheduleDueClock(
      candidate,
      zone || undefined
    )
    if (
      isBriefingScheduleExpressionDue(trimmed, {
        ...clock,
        windowMinutes: 1
      })
    ) {
      return candidate
    }
  }
  return null
}

/**
 * Human status for CrewInterestBrief — paused / due now / next run / unknown.
 * Pure; pass `now` + optional IANA zone (empty → UTC via due-clock helper).
 */
export function formatCrewBriefingScheduleStatus(
  schedule:
    | Pick<
        CrewBriefingSchedule,
        | 'enabled'
        | 'schedule'
        | 'monitorMode'
        | 'autoPaused'
        | 'emptySkipCount'
      >
    | null
    | undefined,
  now: Date,
  timeZone?: string | null,
  opts?: { locale?: string; horizonDays?: number }
): string {
  if (!schedule?.enabled) {
    const paused = isCrewBriefingScheduleAutoPaused(schedule)
      ? CREW_BRIEFING_STATUS_AUTO_PAUSED
      : CREW_BRIEFING_STATUS_PAUSED
    if (!isCrewBriefingScheduleAutoPaused(schedule)) return paused
    const quiet = formatCrewBriefingQuietStreakStatus(schedule)
    return quiet ? `${paused} · ${quiet}` : paused
  }

  const trimmed =
    typeof schedule.schedule === 'string' ? schedule.schedule.trim() : ''
  if (!trimmed || !isValidBriefingScheduleExpression(trimmed)) {
    return CREW_BRIEFING_STATUS_UNKNOWN
  }

  const zone = typeof timeZone === 'string' ? timeZone.trim() : ''
  const clock = resolveCrewBriefingScheduleDueClock(now, zone || undefined)
  let base: string
  if (isCrewBriefingScheduleDue(schedule, clock)) {
    base = CREW_BRIEFING_STATUS_DUE_NOW
  } else {
    const next = previewNextCrewBriefingRunAt(schedule, now, zone || undefined, {
      horizonDays: opts?.horizonDays
    })
    if (!next) {
      base = CREW_BRIEFING_STATUS_UNKNOWN
    } else {
      const formatted = new Intl.DateTimeFormat(opts?.locale, {
        ...(zone ? { timeZone: zone } : {}),
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }).format(next)
      base = `${CREW_BRIEFING_NEXT_RUN_PREFIX} · ${formatted}`
    }
  }

  const quiet = formatCrewBriefingQuietStreakStatus(schedule)
  return quiet ? `${base} · ${quiet}` : base
}
